import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid2 as Grid,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Typography,
  Button,
  Autocomplete,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
  Stack,
  Slider,
  IconButton,
} from '@mui/material'
import { SwitcherOutlined } from '@ant-design/icons'
import { Period, findPeriodById } from 'types/periods'
import { VariableAllocationRow } from 'types/comp-allocations'
import { ExpandedTarget } from 'types/components'
import ComponentField from 'components/ComponentField'
import PercentageRangeInput from 'components/PercentageRangeInput'
import SinglePeriodSelector from 'components/SinglePeriodSelector'
import { useTheme } from '@mui/material/styles'
// import { createIdToPeriodFromList } from 'types/periods'

// Testing
//
interface SimpleEditorContentProps {
  row: VariableAllocationRow
  newRow: boolean
  period: Period | null
  organizationMap: Record<string, string>
  expandedTargets: ExpandedTarget[]
  onSave: (updatedRow: VariableAllocationRow) => void
  onCancel: () => void
  onRowChange?: (updatedRow: VariableAllocationRow) => void
}

// Shared component for displaying selected target details
const SelectedTargetDetails: React.FC<{
  selectedTarget: ExpandedTarget | null
  period: Period | null
  noMargin?: boolean
}> = ({ selectedTarget, period, noMargin = false }) => {
  if (!selectedTarget) return null

  return (
    <Paper sx={{ pt: 2, pb: 1, px: 2, textAlign: 'left', height: '100%', ...(noMargin ? {} : { mt: 3 }) }}>
      <Typography variant="h6" gutterBottom align="left">
        Selected Target Details:
      </Typography>
      <Typography variant="body2" align="left">
        <strong>Component:</strong> {selectedTarget.component.label}
      </Typography>
      <Typography variant="body2" align="left">
        <strong>Period:</strong> {findPeriodById([period!], selectedTarget.target.periodId!)?.label}
      </Typography>
      <Typography variant="body2" align="left">
        <strong>Target:</strong> {selectedTarget.target.value}
      </Typography>
      <Typography variant="body2" align="left">
        <strong>Type:</strong> {selectedTarget.component.componentType}
      </Typography>
    </Paper>
  )
}

// Shared component for achievement hurdle configuration
const AchievementHurdleConfig: React.FC<{
  selectedTarget: ExpandedTarget
  hurdleValue: string | null
  comparisonOperator: string | null
  onHurdleValueChange: (value: any) => void
  onComparisonOperatorChange: (event: SelectChangeEvent<string>) => void
}> = ({ selectedTarget, hurdleValue, comparisonOperator, onHurdleValueChange, onComparisonOperatorChange }) => {
  return (
    <Paper sx={{ pt: 2, pb: 1, px: 3, textAlign: 'left', height: '100%', minHeight: '250px' }}>
      <Typography variant="h6" gutterBottom>
        Achievement Hurdle for {selectedTarget.target.label}:
      </Typography>
      <Box display="flex" alignItems="center" width="100%">
        <FormControl variant="outlined" sx={{ minWidth: 200, mr: 4 }}>
          <InputLabel variant="outlined">Comparison Operator</InputLabel>
          <Select
            value={comparisonOperator || ''}
            onChange={onComparisonOperatorChange}
            label="Comparison Operator"
            style={{ width: '100%', borderRadius: '21px' }}
            variant="outlined"
          >
            {selectedTarget.component.lowerIsBetter
              ? [
                  <MenuItem key="<" value="<">
                    Less Than
                  </MenuItem>,
                  <MenuItem key="<=" value="<=">
                    Less Than or Equal
                  </MenuItem>,
                ]
              : [
                  <MenuItem key=">" value=">">
                    Greater Than
                  </MenuItem>,
                  <MenuItem key=">=" value=">=">
                    Greater Than or Equal
                  </MenuItem>,
                ]}
          </Select>
        </FormControl>
        <Box sx={{ width: '200px' }}>
          <ComponentField
            value={hurdleValue}
            onChange={onHurdleValueChange}
            componentType={selectedTarget.component.componentType}
            name=""
            label="Hurdle Value"
            placeholder="Enter a value"
            currency={'USD'}
            currencyDecimals={2}
            variant="outlined"
            shrink
            pill
          />
        </Box>
      </Box>
      <Typography variant="body2" color="textSecondary" mt={1}>
        Leave blank for no hurdle.
      </Typography>
    </Paper>
  )
}

const getSteps = (selectedTarget: ExpandedTarget | null) => {
  const baseSteps = ['Select Target', 'Adjust Allocations']
  if (selectedTarget?.component.lowerIsBetter) {
    return ['Select Target', 'Set Achievement Formula', 'Adjust Allocations']
  }
  return baseSteps
}

const lookupTargetFromSlug = (slug: string, expandedTargets: ExpandedTarget[]) => {
  return expandedTargets.find((target) => target.target.slug === slug)
}

const extractHurdleInformation = (formulaRequirement: String): { hurdleValue: string; hurdleOperator: string } => {
  let hurdleValue = ''
  let hurdleOperator = ''

  // Check for operators in order of specificity (longer operators first)
  if (formulaRequirement.includes('>=')) {
    const parts = formulaRequirement.split('>=')
    hurdleOperator = '>='
    hurdleValue = parts[1]?.trim() || ''
  } else if (formulaRequirement.includes('<=')) {
    const parts = formulaRequirement.split('<=')
    hurdleOperator = '<='
    hurdleValue = parts[1]?.trim() || ''
  } else if (formulaRequirement.includes('>')) {
    const parts = formulaRequirement.split('>')
    hurdleOperator = '>'
    hurdleValue = parts[1]?.trim() || ''
  } else if (formulaRequirement.includes('<')) {
    const parts = formulaRequirement.split('<')
    hurdleOperator = '<'
    hurdleValue = parts[1]?.trim() || ''
  } else if (formulaRequirement.includes('=')) {
    const parts = formulaRequirement.split('=')
    hurdleOperator = '='
    hurdleValue = parts[1]?.trim() || ''
  }

  return {
    hurdleValue: hurdleValue,
    hurdleOperator: hurdleOperator,
  }
}

// Select the Target and Hurdle Step of the Simple Editor
const SelectTargetStep: React.FC<{
  updatedRow: VariableAllocationRow
  setUpdatedRow: (
    updatedRow: VariableAllocationRow | ((prevRow: VariableAllocationRow) => VariableAllocationRow)
  ) => void
  selectedTarget: ExpandedTarget | null
  onSelectTarget: (target: ExpandedTarget | null) => void
  newRow: boolean
  expandedTargets: ExpandedTarget[]
  organizationMap: Record<string, string>
  period: Period | null
}> = ({
  updatedRow,
  setUpdatedRow,
  selectedTarget,
  onSelectTarget,
  newRow,
  expandedTargets,
  organizationMap,
  period,
}) => {
  const [hurdleValue, setHurdleValue] = useState<string | null>(null)
  const [comparisonOperator, setComparisonOperator] = useState<string | null>(null)

  useEffect(() => {
    // Set the initialize values for hurdle and comparison based in the selected row
    if (updatedRow.formulaRequirements.length > 0) {
      const { hurdleValue, hurdleOperator } = extractHurdleInformation(updatedRow.formulaRequirements[0])
      setHurdleValue(hurdleValue)
      setComparisonOperator(hurdleOperator)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle updating row information when the hurdle value or comparison operator changes
  useEffect(() => {
    // Updated the row based on current selections
    if (!selectedTarget || !hurdleValue || !comparisonOperator) return

    const formulaRequirement = `${selectedTarget.component.slug} ${comparisonOperator} ${hurdleValue}`

    setUpdatedRow((prevRow) => ({
      ...prevRow,
      formulaRequirements: [formulaRequirement],
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hurdleValue, comparisonOperator])

  // Handle updating for the row information when the selected target changes
  useEffect(() => {
    // Implement the logic to handle the selected target
    if (!selectedTarget) return

    setUpdatedRow((prevRow) => ({
      ...prevRow,
      ...(!prevRow.label || prevRow.label === '' ? { label: selectedTarget.target.label } : {}),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTarget])

  const handleSelectTarget = (target: ExpandedTarget | null) => {
    // Implement the logic to handle the selected target
    onSelectTarget(target)
  }

  const handleHurdleValueChange = (value: any) => {
    setHurdleValue(value)
  }

  const handleComparisonOperatorChange = (event: SelectChangeEvent<string>) => {
    setComparisonOperator(event.target.value)
  }

  return (
    <Box sx={{ p: 3 }}>
      {newRow ? (
        <Stack direction="row" spacing={2}>
          <Box sx={{ flexBasis: '30%', flexShrink: 0 }}>
            <SinglePeriodSelector
              key="period-selector"
              periods={[period!]}
              value={updatedRow.periodId!}
              label="Select Period"
              onChange={() => {}}
              rootPeriodOnly={false}
              sortOrder="asc"
              variant="outlined"
              shrink
              pill
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              id="simple-editor-select-target"
              autoHighlight
              options={expandedTargets}
              getOptionLabel={(option) =>
                organizationMap[option.target.organizationId] + ' : ' + option.component.label
              }
              getOptionKey={(option) => option.target.slug}
              isOptionEqualToValue={(option, value) => option.target.slug === value.target.slug}
              value={selectedTarget}
              onChange={(event, newValue) => handleSelectTarget(newValue)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '21px',
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Target"
                  variant="outlined"
                  placeholder="Choose a target..."
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                />
              )}
              renderOption={(props, option: ExpandedTarget) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">{option.component.label}</Typography>
                    <Typography variant="body2">Org: {organizationMap[option.target.organizationId]}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Target: {option.target.value} | Type: {option.component.componentType}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Box>
        </Stack>
      ) : null}

      <Grid container size={{ xs: 12 }} spacing={2} sx={{ alignItems: 'stretch', mt: 3 }}>
        <Grid size={{ xs: 3 }}>
          <SelectedTargetDetails selectedTarget={selectedTarget} period={period} noMargin />
        </Grid>
        <Grid size={{ xs: 9 }}>
          {selectedTarget && (
            <AchievementHurdleConfig
              selectedTarget={selectedTarget}
              hurdleValue={hurdleValue}
              comparisonOperator={comparisonOperator}
              onHurdleValueChange={handleHurdleValueChange}
              onComparisonOperatorChange={handleComparisonOperatorChange}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

const SetAchievementFormulaStep: React.FC = () => (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <Typography variant="h6" gutterBottom>
      Set Achievement Formula Step
    </Typography>
    <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
      <Typography variant="body1" color="text.secondary">
        This is a placeholder for the Set Achievement Formula step content.
      </Typography>
    </Paper>
  </Box>
)

const AdjustAllocationsStep: React.FC<{
  updatedRow: VariableAllocationRow
  setUpdatedRow: (
    updatedRow: VariableAllocationRow | ((prevRow: VariableAllocationRow) => VariableAllocationRow)
  ) => void
  selectedTarget: ExpandedTarget | null
  period: Period | null
}> = ({ updatedRow, setUpdatedRow, selectedTarget, period }) => {
  const theme = useTheme()

  const [toggle, setToggle] = useState(false)

  const [minAllocationPercentage, setMinAllocationPercentage] = useState<number>(
    updatedRow.minAllocationPercentage || 0.1
  )
  const [allocationPercentage, setAllocationPercentage] = useState<number>(updatedRow.allocationPercentage || 0.25)
  const [maxAllocationPercentage, setMaxAllocationPercentage] = useState<number>(
    updatedRow.maxAllocationPercentage || 0.5
  )
  const [sliderValuesForAllocationPercentage, setSliderValuesForAllocationPercentage] = useState<number[]>([
    Math.round(minAllocationPercentage * 100),
    Math.round(allocationPercentage * 100),
    Math.round(maxAllocationPercentage * 100),
  ])

  const [minPayoutPercentage, setMinPayoutPercentage] = useState<number>(updatedRow.minPayout || 1.1)
  const [payoutPercentage, setPayoutPercentage] = useState<number>(updatedRow.defaultPayout || 1.25)
  const [maxPayoutPercentage, setMaxPayoutPercentage] = useState<number>(updatedRow.maxPayout || 1.5)
  const [sliderValuesForMaxPayoutPercentage, setSliderValuesForMaxPayoutPercentage] = useState<number[]>([
    Math.round(minPayoutPercentage * 100),
    Math.round(payoutPercentage * 100),
    Math.round(maxPayoutPercentage * 100),
  ])

  useEffect(() => {
    setSliderValuesForAllocationPercentage([
      Math.round(minAllocationPercentage * 100),
      Math.round(allocationPercentage * 100),
      Math.round(maxAllocationPercentage * 100),
    ])
  }, [minAllocationPercentage, allocationPercentage, maxAllocationPercentage])

  useEffect(() => {
    setSliderValuesForMaxPayoutPercentage([
      Math.round(minPayoutPercentage * 100),
      Math.round(payoutPercentage * 100),
      Math.round(maxPayoutPercentage * 100),
    ])
  }, [minPayoutPercentage, payoutPercentage, maxPayoutPercentage])

  useEffect(() => {
    setUpdatedRow((prevRow) => ({
      ...prevRow,
      minAllocationPercentage: minAllocationPercentage,
      allocationPercentage: allocationPercentage,
      maxAllocationPercentage: maxAllocationPercentage,
      minPayout: minPayoutPercentage,
      defaultPayout: payoutPercentage,
      maxPayout: maxPayoutPercentage,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    minAllocationPercentage,
    allocationPercentage,
    maxAllocationPercentage,
    minPayoutPercentage,
    payoutPercentage,
    maxPayoutPercentage,
  ])

  const handleSliderChangeForAllocationPercentage = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      setMinAllocationPercentage(newValue[0] / 100)
      setAllocationPercentage(newValue[1] / 100)
      setMaxAllocationPercentage(newValue[2] / 100)
      setSliderValuesForAllocationPercentage(newValue)
    }
  }

  const handleSliderChangeForMaxPayoutPercentage = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      setMinPayoutPercentage(newValue[0] / 100)
      setPayoutPercentage(newValue[1] / 100)
      setMaxPayoutPercentage(newValue[2] / 100)
      setSliderValuesForMaxPayoutPercentage(newValue)
    }
  }

  return (
    <Box>
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Grid container size={{ xs: 12 }} spacing={2} sx={{ alignItems: 'stretch' }}>
          <Grid size={{ xs: 3 }}>
            <SelectedTargetDetails selectedTarget={selectedTarget} period={period} noMargin />
          </Grid>
          <Grid size={{ xs: 9 }}>
            <Paper sx={{ pt: 1, pb: 1, px: 3, textAlign: 'left', height: '100%', minHeight: '250px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Target Parameters</Typography>
                <IconButton onClick={() => setToggle(!toggle)}>
                  <SwitcherOutlined />
                </IconButton>
              </Box>
              <Stack direction="column" alignItems="flex-start" spacing={4} sx={{ width: '100%' }}>
                <Box sx={{ width: '100%' }}>
                  {toggle ? (
                    <Stack spacing={1} sx={{ width: '100%' }}>
                      <Typography
                        gutterBottom
                        variant="body1"
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: '0.75rem',
                          lineHeight: '1rem',
                          marginBottom: '10px',
                        }}
                      >
                        Allocation Percentage *
                      </Typography>
                      <Slider
                        key="slider-variable-compensation"
                        value={sliderValuesForAllocationPercentage}
                        onChange={handleSliderChangeForAllocationPercentage}
                        min={0}
                        max={50}
                        marks={[
                          { value: 0, label: '0%' },
                          { value: 50, label: '50%' },
                          {
                            value: sliderValuesForAllocationPercentage[0],
                            label: `${sliderValuesForAllocationPercentage[0]}%`,
                          },
                          {
                            value: sliderValuesForAllocationPercentage[2],
                            label: `${sliderValuesForAllocationPercentage[2]}%`,
                          },
                          {
                            value: sliderValuesForAllocationPercentage[1],
                            label: `${sliderValuesForAllocationPercentage[1]}%`,
                          },
                        ]}
                        step={1}
                        // disabled={compensationModel === 'BONUS_ONLY'}
                        sx={{ width: '100%' }}
                      />
                    </Stack>
                  ) : (
                    <PercentageRangeInput
                      label="Allocation Percentage *"
                      defaultValue={allocationPercentage}
                      setDefaultValue={setAllocationPercentage}
                      minValue={minAllocationPercentage}
                      setMinValue={setMinAllocationPercentage}
                      maxValue={maxAllocationPercentage}
                      setMaxValue={setMaxAllocationPercentage}
                      // disabled={compensationModel === 'BONUS_ONLY'}
                    />
                  )}
                </Box>
                <Box sx={{ width: '100%' }}>
                  {toggle ? (
                    <Stack spacing={1} sx={{ pb: 4, width: '100%' }}>
                      <Typography
                        gutterBottom
                        variant="body1"
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: '0.75rem',
                          lineHeight: '1rem',
                          marginBottom: '10px',
                        }}
                      >
                        Maximum Variable Payout *
                      </Typography>
                      <Slider
                        key="slider-variable-cap"
                        value={sliderValuesForMaxPayoutPercentage}
                        onChange={handleSliderChangeForMaxPayoutPercentage}
                        min={100}
                        max={300}
                        marks={[
                          { value: 100, label: '100%' },
                          { value: 300, label: '300%' },
                          {
                            value: sliderValuesForMaxPayoutPercentage[0],
                            label: `${sliderValuesForMaxPayoutPercentage[0]}%`,
                          },
                          {
                            value: sliderValuesForMaxPayoutPercentage[2],
                            label: `${sliderValuesForMaxPayoutPercentage[2]}%`,
                          },
                          {
                            value: sliderValuesForMaxPayoutPercentage[1],
                            label: `${sliderValuesForMaxPayoutPercentage[1]}%`,
                          },
                        ]}
                        step={1}
                        sx={{ width: '100%' }}
                        size="small"
                      />
                    </Stack>
                  ) : (
                    <PercentageRangeInput
                      label="Maximum Variable Payout *"
                      defaultValue={payoutPercentage}
                      setDefaultValue={setPayoutPercentage}
                      minValue={minPayoutPercentage}
                      setMinValue={setMinPayoutPercentage}
                      maxValue={maxPayoutPercentage}
                      setMaxValue={setMaxPayoutPercentage}
                    />
                  )}
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

const SimpleEditorContent: React.FC<SimpleEditorContentProps> = ({
  row,
  newRow,
  period,
  organizationMap,
  expandedTargets,
  onSave,
  onCancel,
  onRowChange,
}) => {
  const [activeStep, setActiveStep] = useState(0)
  const [selectedTarget, setSelectedTarget] = useState<ExpandedTarget | null>(null)
  const [updatedRow, setUpdatedRow] = useState(row)

  useEffect(() => {
    if (onRowChange) onRowChange(updatedRow)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedRow])
  const steps = getSteps(selectedTarget)

  const handleSelectTarget = (target: ExpandedTarget | null) => {
    setSelectedTarget(target)
    const targetSlug = target?.target.slug || ''
    const componentSlug = target?.component?.slug || ''
    const componentId = target?.component?.id || ''

    setUpdatedRow({
      ...updatedRow,
      targetSlugs: [targetSlug],
      componentSlugs: [componentSlug],
      componentIds: [componentId],
      formulaOptions: [componentSlug + '/' + targetSlug],
    })
    // Reset to step 0 when target changes to recalculate steps
    setActiveStep(0)
  }

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleSave = () => {
    onSave({ ...updatedRow })
  }

  useEffect(() => {
    if (!row || expandedTargets.length === 0) return

    // Try targetSlugs first
    if (row.targetSlugs && row.targetSlugs.length === 1) {
      const result = lookupTargetFromSlug(row.targetSlugs[0], expandedTargets)
      if (result) {
        setSelectedTarget(result)
        return
      }
    }

    // Fall back to extracting target slug from formulaOptions (format: "componentSlug/targetSlug")
    if (row.formulaOptions && row.formulaOptions.length > 0) {
      const firstOption = row.formulaOptions[0]
      const parts = firstOption.split('/')
      if (parts.length === 2) {
        const targetSlug = parts[1]
        const result = lookupTargetFromSlug(targetSlug, expandedTargets)
        if (result) {
          setSelectedTarget(result)
          return
        }
      }
    }

    // Fall back to componentSlugs — match against component slug
    if (row.componentSlugs && row.componentSlugs.length > 0) {
      const result = expandedTargets.find((et) => et.component?.slug === row.componentSlugs![0])
      if (result) {
        setSelectedTarget(result)
      }
    }
  }, [row, expandedTargets])

  const getStepContent = (step: number) => {
    const hasFormulaStep = selectedTarget?.component.lowerIsBetter

    switch (step) {
      case 0:
        return (
          <SelectTargetStep
            updatedRow={updatedRow}
            setUpdatedRow={setUpdatedRow}
            selectedTarget={selectedTarget}
            onSelectTarget={handleSelectTarget}
            newRow={newRow}
            expandedTargets={expandedTargets}
            organizationMap={organizationMap}
            period={period}
          />
        )
      case 1:
        if (hasFormulaStep) {
          return <SetAchievementFormulaStep />
        }
        return (
          <AdjustAllocationsStep
            updatedRow={updatedRow}
            setUpdatedRow={setUpdatedRow}
            selectedTarget={selectedTarget}
            period={period}
          />
        )
      case 2:
        if (hasFormulaStep) {
          return (
            <AdjustAllocationsStep
              updatedRow={updatedRow}
              setUpdatedRow={setUpdatedRow}
              selectedTarget={selectedTarget}
              period={period}
            />
          )
        }
        return <Typography>Unknown step</Typography>
      default:
        return <Typography>Unknown step</Typography>
    }
  }

  const isLastStep = activeStep === steps.length - 1

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '500px' }}>
      <Box sx={{ width: '100%', mt: 2 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box sx={{ flex: 1, mt: 2, mb: 1, overflow: 'auto' }}>{getStepContent(activeStep)}</Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 3,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button disabled={activeStep === 0} onClick={handleBack} color="inherit">
            Back
          </Button>
          {isLastStep ? (
            <Button onClick={handleSave} variant="contained" color="primary">
              Save
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={activeStep === 0 && !selectedTarget}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default SimpleEditorContent
